import { Fragment, useRef, useState } from 'react';
import { LazyLoad } from 'react-observer-api';
import { isMobile } from 'react-device-detect';
import { useAppProvider } from '../../context/app';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import typeOf from 'typeof';
import ObjectMap from './ObjectMap';
import ObjectGallery from './ObjectGallery';
import Button from '../Button';
import Input from '../Input';
import Textarea from '../Textarea';
import ObjectMortgage from './ObjectMortgage';
import { copyToClipboard, declOfNum, getPriceDiff, getTimeInterval, toMoney } from '../../helpers';
import { submitClientRequest } from '../../helpers/clientRequest';
import { useLocationState } from '../../context/location';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { useObjectsDispatch } from '../../context/objects';
import language from '../../data/language.json';
import styles from '../../styles/modules/object/ObjectInformationContent.module.scss';
import FormAgreement from '../FormAgreement';

const Modal = dynamic(() => import('../Modal'));

const objectAddressToText = (loc) => {
  const arr = [];

  if (loc.region && !loc['locality-name']) arr.push(loc.region);
  if (loc['locality-name'] && !loc['sub-locality-name']) arr.push(loc['locality-name']);
  if (loc['sub-locality-name']) arr.push(loc['sub-locality-name']);
  if (loc.street) arr.push(loc.street);
  if (loc.house) arr.push(loc.house);

  return !!arr.length ? arr.join(', ') : 'Местоположение скрыто';
};
const categoryToSEO = (c) => {
  switch (c) {
    default:
    case 'квартира':
      return { text: 'Квартиры', link: 'flats' };

    case 'комната':
      return { text: 'Комнаты', link: 'rooms' };

    case 'дом':
      return { text: 'Дома', link: 'houses' };

    case 'участок':
      return { text: 'Участки', link: 'sites' };

    case 'коммерция':
      return { text: 'Коммерция', link: 'commercial' };
  }
};
const flatsToSEO = (f) => {
  switch (f) {
    case 'студия':
      return { text: 'Студии', link: 'studio' };

    default:
    case 1:
      return { text: '1-комнатные', link: '1k' };

    case 2:
      return { text: '2-комнатные', link: '2k' };

    case 3:
      return { text: '3-комнатные', link: '3k' };

    case 4:
      return { text: '4-комнатные', link: '4k' };

    case 5:
      return { text: '5-комнатные', link: '4k' };
  }
};
const optionToIcon = (t) => {
  return {
    toilet: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M20.39,30.44h-13v.76a6,6,0,0,0,6,6H16v5.45h-.53a2,2,0,0,0-.28,4l.28,0H32.88l.27,0a2,2,0,0,0-.27-4h-.06V37.2h2.62a6,6,0,0,0,6-6v-.76h-21Z" />
        <path d="M42.58,23.42H20.39V8.5a2.71,2.71,0,0,0,0-.29h-13a2.71,2.71,0,0,0,0,.29V27.44h33.4s0,0,0,0h1.81a2,2,0,0,0,0-4Z" />
        <path d="M19.65,5.6h1.07a1.94,1.94,0,0,0,1.18-.39,2,2,0,0,0,.82-1.61,2,2,0,0,0-2-2H7.05a2,2,0,0,0-2,2,2,2,0,0,0,.82,1.61,1.94,1.94,0,0,0,1.18.39h12.6Z" />
      </svg>
    ),
    shower: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M23.18,22.47H33a5.94,5.94,0,0,0,4.24-1.76,5.58,5.58,0,0,0,.94-1.23H18A6,6,0,0,0,23.18,22.47Z" />
        <path d="M33,10.47H30.22V9.55a8,8,0,0,0-8-8H14.62a8,8,0,0,0-7.95,8V44.4a2,2,0,0,0,4,0V9.55a4,4,0,0,1,3.95-4h7.64a4,4,0,0,1,4,4v.92h-3a6,6,0,0,0-6,6H39A6,6,0,0,0,33,10.47Z" />
        <path d="M29.19,27.44h0L28,26.24l-1.19,1.2h0a1.73,1.73,0,1,0,2.93,1.26A1.7,1.7,0,0,0,29.19,27.44Z" />
        <path d="M29.19,33.08h0L28,31.88l-1.19,1.2h0a1.73,1.73,0,1,0,2.93,1.26A1.71,1.71,0,0,0,29.19,33.08Z" />
        <path d="M29.19,38.72h0L28,37.52l-1.19,1.2h0A1.74,1.74,0,0,0,26.27,40a1.74,1.74,0,1,0,2.92-1.26Z" />
        <path d="M24.4,25.34h0l-1.2-1.19L22,25.34h0a1.73,1.73,0,1,0,2.38,0Z" />
        <path d="M24.4,31h0l-1.2-1.19L22,31h0a1.68,1.68,0,0,0-.54,1.25,1.73,1.73,0,1,0,3.46,0A1.68,1.68,0,0,0,24.4,31Z" />
        <path d="M24.4,36.63h0l-1.2-1.19L22,36.62h0a1.68,1.68,0,0,0-.54,1.25,1.73,1.73,0,1,0,3.46,0A1.68,1.68,0,0,0,24.4,36.63Z" />
        <path d="M34,25.34h0l-1.2-1.19L31.6,25.34h0a1.76,1.76,0,0,0-.54,1.26,1.74,1.74,0,1,0,3.47,0A1.76,1.76,0,0,0,34,25.34Z" />
        <path d="M34,31h0l-1.2-1.19L31.6,31h0a1.72,1.72,0,0,0-.54,1.25,1.74,1.74,0,1,0,3.47,0A1.72,1.72,0,0,0,34,31Z" />
        <path d="M34,36.63h0l-1.2-1.19L31.6,36.62h0a1.72,1.72,0,0,0-.54,1.25,1.74,1.74,0,1,0,3.47,0A1.72,1.72,0,0,0,34,36.63Z" />
        <path d="M12.21,33.88H9a2,2,0,1,1,0-4h3.26a2,2,0,0,1,0,4Z" />
      </svg>
    ),
    'auto-repair': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M31.29,36.84A5.32,5.32,0,0,0,28,31.94V29a6,6,0,0,0-6-6H12.18a6,6,0,0,0-6,6v3a5.35,5.35,0,0,0-1.7,1.14,5.24,5.24,0,0,0-1.56,3.75l0,6.61a3,3,0,0,0,6,0v-1.3H25.3v1.3a3,3,0,0,0,6,0S31.29,36.9,31.29,36.84ZM8.69,29a4,4,0,0,1,4-4h8.83a4,4,0,0,1,4,4v2.68H8.69V29ZM8.93,39.9h0a2,2,0,0,1-.35-4,1.67,1.67,0,0,1,.39,0,2,2,0,0,1,0,4Zm10.56,0H14.73a2,2,0,1,1,0-4h4.76a2,2,0,1,1,0,4Zm5.79,0a2,2,0,0,1,0-4,1.5,1.5,0,0,1,.37,0,2,2,0,0,1,1.63,2,2,2,0,0,1-2,2Z" />
        <path d="M44.81,26.06,34.39,15.64,36.16,9,28.87,1.72,22.25,3.49l7.29,7.3-.9,3.34L25.3,15,18,7.74l-1.77,6.61,7.29,7.3,6.63-1.77L40.57,30.3a3,3,0,0,0,4.24-4.24Z" />
      </svg>
    ),
    'automatic-gates': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M26.53,24.5a1,1,0,0,0-1.41,0,1,1,0,0,0,0,1.42l.48.48-.48.49a1,1,0,0,0,0,1.41,1,1,0,0,0,.7.29,1,1,0,0,0,.71-.29l1.19-1.19a1,1,0,0,0,0-1.42Z" />
        <path d="M22.77,24.5a1,1,0,0,0-1.42,0l-1.19,1.19a1,1,0,0,0,0,1.42l1.19,1.19a1,1,0,0,0,.71.29,1,1,0,0,0,.71-.29,1,1,0,0,0,0-1.41l-.49-.49.49-.48A1,1,0,0,0,22.77,24.5Z" />
        <path d="M26.6,19a2.66,2.66,0,1,0-2.66,2.66A2.66,2.66,0,0,0,26.6,19Z" />
        <path d="M24,8.92a9.81,9.81,0,0,0-6.15,2.14,1,1,0,0,0,.62,1.78,1,1,0,0,0,.62-.21,7.9,7.9,0,0,1,9.73-.07A1,1,0,0,0,30,11,9.82,9.82,0,0,0,24,8.92Z" />
        <path d="M24,12.11a6.49,6.49,0,0,0-4.77,2.06,1,1,0,0,0,1.45,1.37,4.57,4.57,0,0,1,6.59,0,1,1,0,0,0,1.44-1.39A6.53,6.53,0,0,0,24,12.11Z" />
        <path d="M24,5.73a13,13,0,0,0-7.46,2.34,1,1,0,0,0,.57,1.83,1,1,0,0,0,.57-.18,11,11,0,0,1,12.49-.1,1,1,0,0,0,1.39-.27A1,1,0,0,0,31.31,8,13,13,0,0,0,24,5.73Z" />
        <path d="M44.48,2a2,2,0,0,0-2,2V7.56L32.7,12.7a1,1,0,0,0-.54.89V40.76a1,1,0,0,0,.83,1l9.49,1.68V44a2,2,0,0,0,4,0V4A2,2,0,0,0,44.48,2ZM36.7,39.1a1,1,0,0,1-1,1h-.13a1,1,0,0,1-.87-1V14.47a1,1,0,0,1,.29-.71l0,0,.48-.25h.21a1,1,0,0,1,1,1Zm4.95.95a1,1,0,0,1-1,1l-.2,0a1,1,0,0,1-.8-1V12.16a1,1,0,0,1,2,0Z" />
        <path d="M15.3,12.7,5.52,7.56V4a2,2,0,1,0-4,0V44a2,2,0,0,0,4,0v-.57L15,41.75a1,1,0,0,0,.83-1V13.59A1,1,0,0,0,15.3,12.7Zm-7,27.35a1,1,0,0,1-.8,1l-.2,0a1,1,0,0,1-1-1V12.16a1,1,0,0,1,2,0Zm5-.95a1,1,0,0,1-.87,1H12.3a1,1,0,0,1-1-1V14.47a1,1,0,0,1,1-1h.23l.45.23,0,0a1,1,0,0,1,.29.71Z" />
      </svg>
    ),
    'inspection-pit': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M40.5,27.59h-3a3,3,0,0,0,2.54-3s0-7.32,0-7.46a6,6,0,0,0-3.69-5.54V7.5a6,6,0,0,0-6-6H17.67a6,6,0,0,0-6,6v4.13A6,6,0,0,0,8,17.17v7.46a3,3,0,0,0,2.54,3h-3a6,6,0,0,0-6,6V40.5a6,6,0,0,0,6,6h33a6,6,0,0,0,6-6V33.59A6,6,0,0,0,40.5,27.59ZM28.7,18.36a2,2,0,0,1-2,2H21.32a2,2,0,0,1,0-4H26.7A2,2,0,0,1,28.7,18.36Zm-15.92,0a2,2,0,1,1,2,2,2.06,2.06,0,0,1-.79-.16A2,2,0,0,1,12.78,18.36Zm20.46-2A2,2,0,0,1,34,20.19a2.06,2.06,0,0,1-.79.16,2,2,0,0,1,0-4ZM14.5,7.83a4,4,0,0,1,4-4h11a4,4,0,0,1,4,4v3.55h-19V7.83ZM32.11,27.59V36.9a6,6,0,0,1-6,6H21.9a6,6,0,0,1-6-6V27.59H11.44a3,3,0,0,0,2.54-3V23.17H34v1.46a3,3,0,0,0,2.54,3Z" />
      </svg>
    ),
    garage: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M45.62,21.5,25.45,2.2l-.07-.07A.45.45,0,0,0,25.22,2a.24.24,0,0,0-.12-.09,1.08,1.08,0,0,0-.33-.18A1.82,1.82,0,0,0,24,1.58h0a1.72,1.72,0,0,0-.39,0,1.86,1.86,0,0,0-.38.12,1.7,1.7,0,0,0-.35.19,1.21,1.21,0,0,0-.26.2L2.38,21.5a2,2,0,0,0-.06,2.83,2,2,0,0,0,2.83.06l.13-.12V40.41a6,6,0,0,0,3.55,5.48V30a5,5,0,0,1,5-5H34.18a5,5,0,0,1,5,5V45.89a6,6,0,0,0,3.55-5.48V24.27l.13.12a2,2,0,0,0,2.82-.06A2,2,0,0,0,45.62,21.5Zm-7.81,1.58H10.19a1,1,0,1,1,0-2H37.81a1,1,0,0,1,0,2ZM10.57,19.2h-.38a1,1,0,0,1,0-2H37.81a1,1,0,0,1,0,2H10.57Z" />
        <path d="M33,34.77V31.62a4,4,0,0,0-4-4H19a4,4,0,0,0-4,4v3.15a3.34,3.34,0,0,0-2.7,3.28v6.5a2,2,0,0,0,1.27,1.86,2,2,0,0,0,.73.14,1.94,1.94,0,0,0,.73-.14,2,2,0,0,0,1.27-1.86V43.48H31.71v1.07a2,2,0,0,0,4,0l0-6.5A3.34,3.34,0,0,0,33,34.77Zm-16-3a2,2,0,0,1,2-2H29a2,2,0,0,1,2,2v3.12H17.05V31.73Zm.16,9.69a1.45,1.45,0,0,1-.93-.33,1.46,1.46,0,0,1-.57-1.17,1.49,1.49,0,0,1,.46-1.07,1.45,1.45,0,0,1,1-.43,1.5,1.5,0,0,1,0,3Zm8.76,0H22a1.5,1.5,0,0,1,0-3H26a1.5,1.5,0,1,1,0,3Zm5,0a1.5,1.5,0,1,1,0-3,1.42,1.42,0,0,1,.9.31,1.48,1.48,0,0,1-.15,2.48A1.37,1.37,0,0,1,31,41.42Z" />
      </svg>
    ),
    'service-three-pl': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M26.58,38.13h-1v1.62a4,4,0,0,0,2.64,3.75v1.44a1.5,1.5,0,0,0,3,0V43.75h8.06v1.19a1.5,1.5,0,0,0,3,0V43.51a4,4,0,0,0,2.65-3.76V38.13H26.58Zm3.13,3.59a1,1,0,1,1,1-1A1,1,0,0,1,29.71,41.72Zm7.38,0h-3.7a1,1,0,0,1,0-2h3.7a1,1,0,0,1,0,2Zm3.68,0a1,1,0,1,1,1-1A1,1,0,0,1,40.77,41.72Z" />
        <path d="M45.5,30a1,1,0,0,0-1,1V32.9h-.6V25.62a4,4,0,0,0-4-4H30.58a4,4,0,0,0-4,4V32.9H26V31a1,1,0,0,0-2,0v2.81a1,1,0,0,0,2,0h.61v2.23H43.9V33.9h.61a1,1,0,0,0,2,0V31A1,1,0,0,0,45.5,30ZM41.71,32.9v1.87h0a2,2,0,0,0-.31-.87,2.13,2.13,0,0,0-1.34-1,1.7,1.7,0,0,0-.55-.08H31a1.7,1.7,0,0,0-.55.08,2.13,2.13,0,0,0-1.34,1,2,2,0,0,0-.31.87h0V30.5a1.88,1.88,0,0,1,1.88-1.88h9.17a1.89,1.89,0,0,1,1.88,1.88Z" />
        <path d="M22.49,18.09A11.18,11.18,0,0,0,13,12.34H12.4a11.17,11.17,0,0,0-9.48,5.75,1.62,1.62,0,0,0-.1.18,11.23,11.23,0,0,0-1.31,5.26v.12A11.15,11.15,0,0,0,2.94,29a1.13,1.13,0,0,0,.12.21,11.17,11.17,0,0,0,9.34,5.52H13a11.15,11.15,0,0,0,9.33-5.52,1.13,1.13,0,0,0,.12-.21,11.1,11.1,0,0,0,.12-10.72A1.62,1.62,0,0,0,22.49,18.09Zm-2.7-.43H17.73a11.69,11.69,0,0,0-1-2.4A9.17,9.17,0,0,1,19.79,17.66Zm-7.28-3.32h.4c1,.15,2,1.36,2.71,3.32H9.8C10.5,15.7,11.53,14.49,12.51,14.34Zm-3.84.92a12.92,12.92,0,0,0-1,2.4h-2A9.25,9.25,0,0,1,8.67,15.26Zm-4.31,4.4H7.2a19.58,19.58,0,0,0-.32,3H3.56A9.26,9.26,0,0,1,4.36,19.66Zm.1,7.94a9,9,0,0,1-.88-3H6.89a18.44,18.44,0,0,0,.35,3Zm1.34,2H7.74a12,12,0,0,0,.94,2.2A9.28,9.28,0,0,1,5.8,29.6Zm7.1,3.12h-.38c-.95-.14-1.94-1.27-2.65-3.12h5.68C14.84,31.45,13.85,32.58,12.9,32.72Zm3.23-5.12H9.29a16.53,16.53,0,0,1-.4-3h7.63A17.51,17.51,0,0,1,16.13,27.6Zm-7.25-5a17.62,17.62,0,0,1,.36-3h6.94a18.76,18.76,0,0,1,.35,3Zm7.84,9.18a11.49,11.49,0,0,0,1-2.21h1.95A9.36,9.36,0,0,1,16.72,31.81ZM21,27.6H18.18a19.67,19.67,0,0,0,.34-3h3.32A9,9,0,0,1,21,27.6Zm-2.43-5a21,21,0,0,0-.31-3h2.84a9.26,9.26,0,0,1,.8,3Z" />
        <path d="M25,7.9c0,.09,0,.18,0,.28l-.19,11.9a1,1,0,0,0,1.94.2L28.49,11l5.79,5.16,0,0c0,.41.1,3.53.1,3.53a.54.54,0,0,0,1.06.11l.75-3.19,3.19-.75a.53.53,0,0,0-.11-1.05l-3.53-.11s0,0,0-.05L30.61,8.88,39.89,7.1a1,1,0,0,0-.2-1.93l-11.9.19a2.36,2.36,0,0,0-.28,0L24.79,2.35A2,2,0,1,0,22,5.18Z" />
      </svg>
    ),
    alarm: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M45.62,21.59,25.38,2.22l-.12-.1L25.1,2l-.17-.1-.18-.08a1.06,1.06,0,0,0-.19-.06l-.18-.05-.19,0-.19,0-.19,0-.19,0-.18.05a1.06,1.06,0,0,0-.19.06l-.17.08L22.9,2l-.15.12-.13.1L2.38,21.59A2,2,0,0,0,3.76,25a2,2,0,0,0,1.39-.55l.13-.13V40.5a6,6,0,0,0,6,6H36.73a6,6,0,0,0,6-6V24.35l.12.13a2,2,0,0,0,2.83-.07A2,2,0,0,0,45.62,21.59ZM15.14,38.23a1,1,0,0,1-.57.18,1,1,0,0,1-.82-.43,15,15,0,0,1-.13-17A1,1,0,0,1,15,20.75a1,1,0,0,1,.27,1.38,13,13,0,0,0,.11,14.7A1,1,0,0,1,15.14,38.23Zm2.73-1.59a1,1,0,0,1-1.41-.16,11.37,11.37,0,0,1-.1-14,1,1,0,1,1,1.58,1.22A9.37,9.37,0,0,0,18,35.24,1,1,0,0,1,17.87,36.64ZM21,34.85a1,1,0,0,1-1.41,0,7.48,7.48,0,0,1-.08-10.79,1,1,0,1,1,1.4,1.44,5.46,5.46,0,0,0,.05,7.89A1,1,0,0,1,21,34.85Zm3-2.93a2.43,2.43,0,1,1,2.43-2.43A2.43,2.43,0,0,1,24,31.92Zm3.82,3.24a1,1,0,0,1-.7-1.72,5.46,5.46,0,0,0-.05-7.89,1,1,0,0,1,0-1.42,1,1,0,0,1,1.41,0,7.48,7.48,0,0,1,.08,10.79A1,1,0,0,1,27.82,35.16Zm3,1.7a1,1,0,0,1-.61-.21,1,1,0,0,1-.18-1.4A9.37,9.37,0,0,0,30,23.74a1,1,0,0,1,.16-1.4,1,1,0,0,1,1.41.16A11.39,11.39,0,0,1,34,29.55a11.24,11.24,0,0,1-2.35,6.92A1,1,0,0,1,30.85,36.86ZM34.38,38a1,1,0,0,1-.83.44,1,1,0,0,1-.83-1.56,13,13,0,0,0-.11-14.7,1,1,0,0,1,.25-1.4,1,1,0,0,1,1.39.25,15,15,0,0,1,.13,17Z" />
      </svg>
    ),
    'flat-alarm': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M45.62,21.59,25.38,2.22l-.12-.1L25.1,2l-.17-.1-.18-.08a1.06,1.06,0,0,0-.19-.06l-.18-.05-.19,0-.19,0-.19,0-.19,0-.18.05a1.06,1.06,0,0,0-.19.06l-.17.08L22.9,2l-.15.12-.13.1L2.38,21.59A2,2,0,0,0,3.76,25a2,2,0,0,0,1.39-.55l.13-.13V40.5a6,6,0,0,0,6,6H36.73a6,6,0,0,0,6-6V24.35l.12.13a2,2,0,0,0,2.83-.07A2,2,0,0,0,45.62,21.59ZM15.14,38.23a1,1,0,0,1-.57.18,1,1,0,0,1-.82-.43,15,15,0,0,1-.13-17A1,1,0,0,1,15,20.75a1,1,0,0,1,.27,1.38,13,13,0,0,0,.11,14.7A1,1,0,0,1,15.14,38.23Zm2.73-1.59a1,1,0,0,1-1.41-.16,11.37,11.37,0,0,1-.1-14,1,1,0,1,1,1.58,1.22A9.37,9.37,0,0,0,18,35.24,1,1,0,0,1,17.87,36.64ZM21,34.85a1,1,0,0,1-1.41,0,7.48,7.48,0,0,1-.08-10.79,1,1,0,1,1,1.4,1.44,5.46,5.46,0,0,0,.05,7.89A1,1,0,0,1,21,34.85Zm3-2.93a2.43,2.43,0,1,1,2.43-2.43A2.43,2.43,0,0,1,24,31.92Zm3.82,3.24a1,1,0,0,1-.7-1.72,5.46,5.46,0,0,0-.05-7.89,1,1,0,0,1,0-1.42,1,1,0,0,1,1.41,0,7.48,7.48,0,0,1,.08,10.79A1,1,0,0,1,27.82,35.16Zm3,1.7a1,1,0,0,1-.61-.21,1,1,0,0,1-.18-1.4A9.37,9.37,0,0,0,30,23.74a1,1,0,0,1,.16-1.4,1,1,0,0,1,1.41.16A11.39,11.39,0,0,1,34,29.55a11.24,11.24,0,0,1-2.35,6.92A1,1,0,0,1,30.85,36.86ZM34.38,38a1,1,0,0,1-.83.44,1,1,0,0,1-.83-1.56,13,13,0,0,0-.11-14.7,1,1,0,0,1,.25-1.4,1,1,0,0,1,1.39.25,15,15,0,0,1,.13,17Z" />
      </svg>
    ),
    'fire-alarm': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M45.62,21.59,25.38,2.22l-.12-.1L25.1,2l-.17-.1-.18-.08a1.06,1.06,0,0,0-.19-.06l-.18-.05-.19,0-.19,0-.19,0-.19,0-.18.05a1.06,1.06,0,0,0-.19.06l-.17.08L22.9,2l-.15.12-.13.1L2.38,21.59A2,2,0,0,0,3.76,25a2,2,0,0,0,1.39-.55l.13-.13V40.5a6,6,0,0,0,6,6H36.73a6,6,0,0,0,6-6V24.35l.12.13a2,2,0,0,0,2.83-.07A2,2,0,0,0,45.62,21.59ZM15.14,38.23a1,1,0,0,1-.57.18,1,1,0,0,1-.82-.43,15,15,0,0,1-.13-17A1,1,0,0,1,15,20.75a1,1,0,0,1,.27,1.38,13,13,0,0,0,.11,14.7A1,1,0,0,1,15.14,38.23Zm2.73-1.59a1,1,0,0,1-1.41-.16,11.37,11.37,0,0,1-.1-14,1,1,0,1,1,1.58,1.22A9.37,9.37,0,0,0,18,35.24,1,1,0,0,1,17.87,36.64ZM21,34.85a1,1,0,0,1-1.41,0,7.48,7.48,0,0,1-.08-10.79,1,1,0,1,1,1.4,1.44,5.46,5.46,0,0,0,.05,7.89A1,1,0,0,1,21,34.85Zm3-2.93a2.43,2.43,0,1,1,2.43-2.43A2.43,2.43,0,0,1,24,31.92Zm3.82,3.24a1,1,0,0,1-.7-1.72,5.46,5.46,0,0,0-.05-7.89,1,1,0,0,1,0-1.42,1,1,0,0,1,1.41,0,7.48,7.48,0,0,1,.08,10.79A1,1,0,0,1,27.82,35.16Zm3,1.7a1,1,0,0,1-.61-.21,1,1,0,0,1-.18-1.4A9.37,9.37,0,0,0,30,23.74a1,1,0,0,1,.16-1.4,1,1,0,0,1,1.41.16A11.39,11.39,0,0,1,34,29.55a11.24,11.24,0,0,1-2.35,6.92A1,1,0,0,1,30.85,36.86ZM34.38,38a1,1,0,0,1-.83.44,1,1,0,0,1-.83-1.56,13,13,0,0,0-.11-14.7,1,1,0,0,1,.25-1.4,1,1,0,0,1,1.39.25,15,15,0,0,1,.13,17Z" />
      </svg>
    ),
    'car-wash': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M44.86,42.54H38.42a3,3,0,0,0,2.17-2.88V32.77s0-.09,0-.14,0-.28,0-.43a6,6,0,0,0-3.69-5.53V22.53a6,6,0,0,0-6-6H18.23a6,6,0,0,0-6,6v4.14A6,6,0,0,0,8.54,32.2c0,.14,0,.27,0,.4s0,.11,0,.17v6.89a3,3,0,0,0,2.18,2.88H4.5a2,2,0,0,0,0,4H44.86a2,2,0,0,0,0-4ZM35.8,33.39a2,2,0,1,1-2-2A2,2,0,0,1,35.8,33.39ZM15.07,22.86a4,4,0,0,1,4-4h11a4,4,0,0,1,4,4v3.55h-19ZM29.26,33.4a2,2,0,0,1-2,2H21.88a2,2,0,0,1,0-4h5.38A2,2,0,0,1,29.26,33.4Zm-13.92-2a2,2,0,1,1-2,2A2,2,0,0,1,15.34,31.4Zm-.79,8.26V38.2h20v1.46a3,3,0,0,0,2.18,2.88H12.37A3,3,0,0,0,14.55,39.66Z" />
        <path d="M4.5,5.67H20.21L6.72,15.7a1,1,0,0,0,.6,1.8,1,1,0,0,0,.6-.19L21.14,7.47l-3.82,6.4a1,1,0,0,0,.35,1.37,1.06,1.06,0,0,0,.51.14,1,1,0,0,0,.86-.49L23.57,7.3v7.08a1,1,0,1,0,2,0V7.3l4.53,7.59a1,1,0,0,0,.86.49,1.1,1.1,0,0,0,.52-.14,1,1,0,0,0,.34-1.37L28,7.47l13.23,9.84a1,1,0,0,0,1.4-.21,1,1,0,0,0-.21-1.4l-13.48-10H44.86a2,2,0,0,0,0-4H4.5a2,2,0,0,0,0,4Z" />
        <circle cx="44.86" cy="20.62" r="2.13" />
        <path d="M4.2,32.54a2.13,2.13,0,1,0-2.13-2.13A2.12,2.12,0,0,0,4.2,32.54Z" />
        <path d="M3.38,9.49a1.58,1.58,0,0,0,2.24,2.23A1.58,1.58,0,0,0,3.38,9.49Z" />
        <path d="M39.48,24.14a1.58,1.58,0,1,0,2.24,2.23,1.58,1.58,0,0,0-2.24-2.23Z" />
        <path d="M8.28,21.5A1.2,1.2,0,0,0,10,23.19,1.2,1.2,0,1,0,8.28,21.5Z" />
        <path d="M39.76,8.35A1.2,1.2,0,0,0,41.45,10a1.2,1.2,0,1,0-1.69-1.69Z" />
      </svg>
    ),
    cctv: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M11.86,7.73a6,6,0,0,0-.18,8.21l2.53,2.81h0a5.45,5.45,0,0,0-.56.42,5,5,0,0,0,.71,8.05l-1.12,7.18H5.79v-3a2,2,0,0,0-4,0v10a2,2,0,0,0,4,0v-3H15a2,2,0,0,0,2-1.69l1.38-8.88a5.1,5.1,0,0,0,2.13-1.12,4.6,4.6,0,0,0,.47-.52h0l6.92,7.68L32,30.12Z" />
        <path d="M41.4,21.68,25.24,3.73a6,6,0,0,0-8.48-.44L14.82,5,35,27.44Z" />
        <path d="M45,25.71l-1-1.06L30.53,36.86l.95,1.06a6,6,0,0,0,8.47.44l4.64-4.18A6,6,0,0,0,45,25.71Z" />
      </svg>
    ),
    railway: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M38.2,13.22a6,6,0,0,0-6-6H26.31V5.52h3.81a2,2,0,0,0,0-4H18.5a2,2,0,0,0,0,4h3.81v1.7H15.76a6,6,0,0,0-6,6V24.14H38.2Zm-3.64,4.26a4,4,0,0,1-4,4H17.4a4,4,0,0,1-4-4V14.34a4,4,0,0,1,4-4H30.56a4,4,0,0,1,4,4Z" />
        <path d="M44.86,44.26l-7.07-4.49-3.53-2.24A6,6,0,0,0,38.2,31.9V28.14H9.76V31.9a6,6,0,0,0,4,5.64L3.13,44.29a1,1,0,0,0-.3,1.38,1,1,0,0,0,1.38.3l1-.61H42.86l.93.59a1,1,0,0,0,1.07-1.69ZM33.79,39.6H14.2l2.67-1.7H31.11Zm-3.22-8a2,2,0,1,1,0,2.83A2,2,0,0,1,30.57,31.55Zm-16,0a2,2,0,1,1,0,2.83A2,2,0,0,1,14.56,31.55ZM19.66,33a2,2,0,0,1,2-2H26.3a2,2,0,0,1,2,2,1.82,1.82,0,0,1-.13.7A2,2,0,0,1,26.47,35h-5a2,2,0,0,1-1.71-1.28A2.08,2.08,0,0,1,19.66,33ZM8.31,43.36l2.76-1.76H36.94l2.77,1.76Z" />
      </svg>
    ),
    ramp: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M46.5,26.41V6.82a2.13,2.13,0,0,0,0-.25,1.89,1.89,0,0,0-.06-.88,2,2,0,0,0-2.52-1.34l-41,12.51-.1,0L2.65,17l-.19.11-.14.09-.17.14L2,17.43l-.15.17-.09.14a1.74,1.74,0,0,0-.1.19,1.11,1.11,0,0,0-.07.16l-.06.21a1,1,0,0,0,0,.17,1.64,1.64,0,0,0,0,.23s0,.06,0,.09V38.37a2.3,2.3,0,0,0,0,.26l0,.25v1.57a6.05,6.05,0,0,0,6.06,6H40.45a6.05,6.05,0,0,0,6-6v-14Zm-34.76-8v2.92L5.54,23.2V20.28ZM22,15.26v2.92l-6.2,1.9V17.16Zm10.24-3.12v2.92L26,17V14ZM15.78,33.06V24.3L22,22.4v8.76ZM26,29.93V21.17l6.2-1.89V28ZM5.54,27.42l6.2-1.89v8.76l-6.2,1.89Zm36.92-2.51-6.2,1.9V18.05l6.2-1.9Zm0-13-6.2,1.89V10.9L42.46,9Z" />
      </svg>
    ),
    'truck-entrance': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M5.53,25.53a2,2,0,0,0,2-1.81h.9v3.72H39.6V23.72h.9a2,2,0,0,0,4-.19V18.47a2,2,0,0,0-4,0v3.25H39.6V7.5a6,6,0,0,0-6-6H14.41a6,6,0,0,0-6,6V21.72H7.53V18.47a2,2,0,0,0-4,0v5.06A2,2,0,0,0,5.53,25.53Zm6.83-7.41a4,4,0,0,1,4-4H31.64a4,4,0,0,1,4,4v7.07h0a4,4,0,0,0-4-3.51H16.36a4,4,0,0,0-4,3.51h0Z" />
        <path d="M6.58,31.44v3.91a6,6,0,0,0,2.95,5.17v3a3,3,0,0,0,6,0V41.35H32.48V43.5a3,3,0,0,0,6,0v-3a6,6,0,0,0,3-5.17V31.44ZM12.53,38a2,2,0,1,1,2-2A2,2,0,0,1,12.53,38Zm14.18,0H21.33a2,2,0,0,1,0-4h5.38a2,2,0,0,1,0,4Zm8.77,0a2,2,0,1,1,2-2A2,2,0,0,1,35.48,38Z" />
      </svg>
    ),
    'freight-elevator': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <rect x="25.98" y="14.39" width="7.85" height="32.11" />
        <rect x="14.13" y="14.39" width="7.85" height="32.11" />
        <path d="M40.5,1.5H7.5a6,6,0,0,0-6,6v33a6,6,0,0,0,6,6h2.63V14.39a4,4,0,0,1,4-4h19.7a4,4,0,0,1,4,4V46.5H40.5a6,6,0,0,0,6-6V7.5A6,6,0,0,0,40.5,1.5ZM25.19,8H16.77a2,2,0,0,1,0-4h8.42a2,2,0,0,1,0,4Zm5.22,0a2,2,0,1,1,2-2A2,2,0,0,1,30.41,8Zm13.75,22.3L43,31.44a1,1,0,0,1-.71.29,1,1,0,0,1-.71-.29l-1.19-1.19a1,1,0,0,1,1.42-1.42l.48.49.49-.49a1,1,0,0,1,1.41,0A1,1,0,0,1,44.16,30.25Zm0-3.76a1,1,0,0,1-1.41,0L42.26,26l-.48.49a1,1,0,0,1-.71.29,1,1,0,0,1-.71-.29,1,1,0,0,1,0-1.42l1.19-1.19a1,1,0,0,1,1.42,0l1.19,1.19A1,1,0,0,1,44.16,26.49Z" />
      </svg>
    ),
    'office-warehouse': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <polygon points="30.72 45.5 37.55 45.5 37.55 38.67 36.12 38.67 30.72 38.67 30.72 45.5" />
        <polygon points="28.29 31.62 28.29 33.62 28.29 36.67 29.72 36.67 35.12 36.67 35.12 33.62 35.12 31.62 35.12 29.84 28.29 29.84 28.29 31.62" />
        <rect x="6.5" y="10.92" width="35" height="2.17" />
        <path d="M38.5,6.5H9.5A3,3,0,0,0,6.56,8.92H41.44A3,3,0,0,0,38.5,6.5Z" />
        <path d="M37.12,33.62v3.05h1.43a1,1,0,0,1,1,1V46.5h1a6.64,6.64,0,0,0,1-.09V33.62Z" />
        <polygon points="21.88 38.67 21.88 45.5 28.72 45.5 28.72 38.67 27.29 38.67 21.88 38.67" />
        <path d="M6.5,46.41a6.52,6.52,0,0,0,1,.09H19.88V37.67a1,1,0,0,1,1-1h5.41V33.62H6.5Z" />
        <path d="M40.5,1.5H7.5a6,6,0,0,0-6,6v33a6,6,0,0,0,3,5.19V9.5a5,5,0,0,1,5-5h29a5,5,0,0,1,5,5V45.69a6,6,0,0,0,3-5.19V7.5A6,6,0,0,0,40.5,1.5Z" />
        <path d="M6.5,31.62H9V27.44H8.84a1,1,0,0,1,0-2h6.34V24.15H12.46a1,1,0,0,1-1-1v-4a1,1,0,0,1,1-1h7.43a1,1,0,0,1,1,1v4a1,1,0,0,1-1,1H17.18v1.29h6.33a1,1,0,0,1,0,2h-.13v4.18h2.91V28.84a1,1,0,0,1,1-1h8.83a1,1,0,0,1,1,1v2.78H41.5V15.09H6.5Z" />
        <rect x="10.97" y="27.44" width="10.41" height="4.18" />
        <rect x="13.46" y="20.14" width="5.43" height="2.01" />
      </svg>
    ),
    'access-control-system': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M35,2.61H13a6,6,0,0,0-6,6v18H41v-18A6,6,0,0,0,35,2.61ZM14.92,22A9.81,9.81,0,0,1,24,15.9a5.13,5.13,0,1,1,5.12-5.13A5.13,5.13,0,0,1,24,15.9,9.79,9.79,0,0,1,33.07,22Z" />
        <path d="M7,39.39a6,6,0,0,0,6,6H35a6,6,0,0,0,6-6V30.62H7Zm25-4a1,1,0,0,1,2,0V37a1,1,0,1,1-2,0Zm-4.48,0a1,1,0,0,1,2,0v3.48a1,1,0,1,1-2,0Zm-4.47,0a1,1,0,0,1,2,0v5.38a1,1,0,1,1-2,0Zm-4.48,0a1,1,0,0,1,2,0v3.48a1,1,0,0,1-2,0Zm-4.48,0a1,1,0,0,1,2,0V37a1,1,0,0,1-2,0Z" />
      </svg>
    ),
    'twenty-four-seven': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <polygon points="20.43 33.4 20.43 27.95 19.01 33.4 20.43 33.4" />
        <path d="M42.75,19.78h0L26.81,7.58a2.94,2.94,0,0,0,.05-.51,2.59,2.59,0,1,0-5.18,0,2.5,2.5,0,0,0,.05.47L5.27,19.77h0a6,6,0,0,0-3.77,5.57V37.52a6,6,0,0,0,6,6h33a6,6,0,0,0,6-6V25.34A6,6,0,0,0,42.75,19.78ZM22.82,9.21a2.57,2.57,0,0,0,1.45.45,2.52,2.52,0,0,0,1.41-.43L38.89,19.34H9.19ZM14.57,35.65v2.19h-7V36c2.08-3.41,3.32-5.51,3.71-6.32a4.66,4.66,0,0,0,.59-1.9,1.35,1.35,0,0,0-.18-.77.68.68,0,0,0-1.08,0,2.38,2.38,0,0,0-.18,1.13v1.22H7.54v-.47a9.48,9.48,0,0,1,.11-1.69A2.92,2.92,0,0,1,8.19,26a2.72,2.72,0,0,1,1.13-.91A4.08,4.08,0,0,1,11,24.79a4,4,0,0,1,2.89.94,3.2,3.2,0,0,1,1,2.4,5.63,5.63,0,0,1-.55,2.32,45.29,45.29,0,0,1-3.24,5.2Zm10-.07h-.91v2.26h-3.2V35.58H16.63V33.4L19.39,25h4.23V33.4h.91Zm4.23,2.5H26.34l3.81-13.29h2.4ZM40.47,27.82l-2,10H35.25l2.27-10.5H34.36V25h6.11Z" />
      </svg>
    ),
    'fire-alarm': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M45.62,21.59,25.38,2.22l-.13-.1L25.1,2l-.18-.1-.17-.08a1.06,1.06,0,0,0-.19-.06l-.18-.05-.19,0-.19,0-.19,0-.19,0-.18.05a1.06,1.06,0,0,0-.19.06l-.17.08L22.9,2l-.16.12-.12.1L2.38,21.59a2,2,0,0,0,2.77,2.89l.13-.13V40.5a6,6,0,0,0,6,6H36.73a6,6,0,0,0,6-6V24.35l.12.13a2,2,0,0,0,2.77-2.89Zm-28.46,22a7.51,7.51,0,0,1-5.17-13h0l5.18-5.18,5.18,5.18h0a7.51,7.51,0,0,1-5.17,13Zm6.38-17.92A2.43,2.43,0,1,1,26,23.27,2.42,2.42,0,0,1,23.54,25.69Zm3.82,3.25a1,1,0,0,1-.7-1.72,5.46,5.46,0,0,0-.05-7.89,1,1,0,0,1,0-1.42,1,1,0,0,1,1.41,0,7.48,7.48,0,0,1,.08,10.79A1,1,0,0,1,27.36,28.94Zm3,1.7a1,1,0,0,1-.61-.21A1,1,0,0,1,29.6,29a9.37,9.37,0,0,0-.09-11.51,1,1,0,0,1,1.57-1.24,11.37,11.37,0,0,1,2.45,7,11.24,11.24,0,0,1-2.35,6.92A1,1,0,0,1,30.39,30.64Zm3.53,1.11a1,1,0,0,1-.83.44,1,1,0,0,1-.56-.18,1,1,0,0,1-.27-1.39,13,13,0,0,0-.11-14.7,1,1,0,1,1,1.64-1.14,15,15,0,0,1,.13,17Z" />
        <path d="M20.89,33.52l-3.73-3.73-3.74,3.73h0a5.43,5.43,0,1,0,7.45,0Zm-3.73,8.59a2.93,2.93,0,0,1-2-5h0l2-2,2,2h0a2.87,2.87,0,0,1,.92,2.12A2.93,2.93,0,0,1,17.16,42.11Z" />
      </svg>
    ),
    ventilation: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M40.5,1.5H7.5a6,6,0,0,0-6,6v33a6,6,0,0,0,6,6h33a6,6,0,0,0,6-6V7.5A6,6,0,0,0,40.5,1.5Zm-.56,3.71a2,2,0,1,1,0,2.85A2,2,0,0,1,39.94,5.21Zm-34.73,0a2,2,0,1,1,0,2.85A2,2,0,0,1,5.21,5.21ZM8.06,42.79a2,2,0,1,1,0-2.85A2,2,0,0,1,8.06,42.79Zm34.73,0a2,2,0,1,1,0-2.85A2,2,0,0,1,42.79,42.79ZM24,43.88A19.88,19.88,0,1,1,43.88,24,19.88,19.88,0,0,1,24,43.88Z" />
        <path d="M37.84,16.06l.76.16a5.84,5.84,0,0,0-9-5.92h0a6.43,6.43,0,0,0-1.73,1.87c-1.06,1.69-.88,4.39-.81,6.69a5.71,5.71,0,0,0-2.27-.8c-.13-3.78.33-7.82,4-9.87-4.7-6.67-14.3,1-9.22,7,.91.95,1.94,2,2.94,3a5.79,5.79,0,0,0-2.15,1c-2.81-2.62-5.3-5.67-4.16-9.83a5.83,5.83,0,0,0-6.51,7.84.08.08,0,0,0,0,0C11.06,21.1,15.29,21,18.85,21a5.6,5.6,0,0,0-.78,2.24c-3.78.14-7.84-.31-9.88-4C3.13,22.55,6.34,30.42,12,29.78h0c2.16.11,4.41-2.4,6.2-4.31a5.85,5.85,0,0,0,1,2.16c-2.51,2.75-5.89,5.35-9.81,4.16-1.2,5.79,6.34,9.15,9.92,5.07,1.93-1.67,1.67-5,1.59-7.71a5.8,5.8,0,0,0,2.26.79c.15,3.8-.3,7.86-4,9.89,3,4.48,10.25,2.55,10.61-2.72a4.61,4.61,0,0,0,0-1.05c0-2.25-2.43-4.45-4.32-6.25a5.61,5.61,0,0,0,2.16-1c2.76,2.55,5.34,5.82,4.17,9.81A5.73,5.73,0,0,0,37,37h0c2.7-2.89,1.93-6.28-.35-8.54a6.54,6.54,0,0,0-5.05-1.36v0H29.14a5.52,5.52,0,0,0,.78-2.21c2.31,0,5.12-.21,7,1.09h0a8.75,8.75,0,0,1,2.86,2.88c3.75-2.34,3.21-8.53-.93-10.14s-6.26.94-9.08,3.86a5.94,5.94,0,0,0-1-2.14C31.8,17.29,33.5,15.6,37.84,16.06ZM24,27.06A3.06,3.06,0,1,1,27.06,24,3.06,3.06,0,0,1,24,27.06Z" />
      </svg>
    ),
    'water-supply': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M2.18,17.79a6,6,0,0,0,6,6h4V12a.41.41,0,0,1,0-.16h-4A6,6,0,0,0,2.18,17.79Z" />
        <path d="M29.8,11.79H21.17a.41.41,0,0,1,0,.16V23.79H29.8a4,4,0,0,1,4,4v2.57a6,6,0,0,0,12,0V27.79A16,16,0,0,0,29.8,11.79Z" />
        <path d="M22.61,8.61a2,2,0,1,0,0-4H19.74a3,3,0,0,0-6,0H10.87a2,2,0,0,0,0,4h2.87V24.52a2.91,2.91,0,0,0,.45,1.57,3,3,0,0,0,5.55-1.57V8.61Z" />
        <path d="M42.3,40.14l-2.5-2.5-2.5,2.5h0a3.63,3.63,0,1,0,5,0Z" />
      </svg>
    ),
    'heating-supply': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M40,22.61a5,5,0,0,0,5-5V16.37a1,1,0,1,0-2,0v1.24a3,3,0,0,1-3,3h-.1V17.79a3.5,3.5,0,0,0-7,0v2.82H31.63V17.79a3.5,3.5,0,0,0-7,0v2.82H23.37V17.79a3.5,3.5,0,0,0-7,0v2.82H15.1V17.79a3.5,3.5,0,0,0-7,0v2.82H4a1,1,0,0,0,0,2H8.1v15.5H8a5,5,0,0,0-5,5v1.3a1,1,0,0,0,2,0v-1.3a3,3,0,0,1,3-3h.1v2.82a3.5,3.5,0,0,0,7,0V40.11h1.27v2.82a3.5,3.5,0,0,0,7,0V40.11h1.26v2.82a3.5,3.5,0,0,0,7,0V40.11H32.9v2.82a3.5,3.5,0,0,0,7,0V40.11H44a1,1,0,0,0,0-2H39.9V22.61ZM16.37,38.11H15.1V22.61h1.27Zm8.26,0H23.37V22.61h1.26Zm8.27,0H31.63V22.61H32.9Z" />
        <path d="M18.25,8.12l.89.89-1,1a1,1,0,0,0,1.41,1.42l1-1a2,2,0,0,0,0-2.82l-.89-.89.94-.94a2,2,0,0,0,0-2.83L19.43,1.76a1,1,0,0,0-1.41,0,1,1,0,0,0,0,1.42l1.17,1.17-.94.94A2,2,0,0,0,18.25,8.12Z" />
        <path d="M23.66,8.12l.89.89-1,1a1,1,0,0,0,1.41,1.42l1-1A2,2,0,0,0,26,7.6l-.89-.89L26,5.77a2,2,0,0,0,0-2.83L24.84,1.76a1,1,0,1,0-1.41,1.42L24.6,4.35l-.94.94A2,2,0,0,0,23.66,8.12Z" />
        <path d="M29.07,8.12,30,9l-1,1a1,1,0,0,0,0,1.42,1,1,0,0,0,.71.29,1,1,0,0,0,.71-.29l1-1a2,2,0,0,0,0-2.82l-.89-.89.94-.94a2,2,0,0,0,0-2.83L30.25,1.76a1,1,0,0,0-1.42,1.42L30,4.35l-.94.94A2,2,0,0,0,29.07,8.12Z" />
      </svg>
    ),
    pmg: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M40.26,26.65a5.09,5.09,0,0,0-5.08-5.08H27.45V18.78a7.36,7.36,0,1,0-6.9,0v2.79H12.82a5.09,5.09,0,0,0-5.08,5.08v1H40.26Z" />
        <path d="M10.89,38.16H37.13a5.94,5.94,0,0,0,5.94-5.94V29.56H4.93V32.2A6,6,0,0,0,10.89,38.16Z" />
        <path d="M45,41.08H3a2,2,0,0,0,0,4H45a2,2,0,0,0,0-4Z" />
      </svg>
    ),
    pool: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M40.5,23.27H32.81V7.41a3.9,3.9,0,0,1,7.79,0,1,1,0,1,0,2,0,5.9,5.9,0,0,0-11.79,0V9.72H19.09V7.41a3.9,3.9,0,0,1,7.79,0,1,1,0,0,0,2,0,5.9,5.9,0,0,0-11.79,0V23.27H7.5a6,6,0,0,0-6,6V40.54a6,6,0,0,0,6,6h33a6,6,0,0,0,6-6V29.27A6,6,0,0,0,40.5,23.27ZM19.09,11.72H30.81v5H19.09Zm0,7H30.81v4.6H19.09Zm20.45,21.8a1.51,1.51,0,0,1-2.12,0l-2.74-2.75a.31.31,0,0,0-.44,0l-2.17,2.17a3.32,3.32,0,0,1-4.68,0l-1.46-1.47a.27.27,0,0,0-.44,0l-1.73,1.73a3.25,3.25,0,0,1-2.34,1,3.28,3.28,0,0,1-2.34-1l-1.7-1.7a.28.28,0,0,0-.44,0l-1.61,1.61a3.32,3.32,0,0,1-4.68,0l-2.19-2.2a1.5,1.5,0,1,1,2.12-2.12L12.77,38a.31.31,0,0,0,.44,0l1.61-1.61a3.31,3.31,0,0,1,4.68,0L21.2,38a.27.27,0,0,0,.44,0l1.73-1.73a3.39,3.39,0,0,1,4.68,0l1.46,1.46a.31.31,0,0,0,.44,0l2.17-2.17a3.31,3.31,0,0,1,4.68,0l2.74,2.75A1.49,1.49,0,0,1,39.54,40.47Zm0-6.76a1.49,1.49,0,0,1-2.12,0L34.68,31a.31.31,0,0,0-.44,0l-2.17,2.17a3.31,3.31,0,0,1-4.68,0l-1.46-1.46a.31.31,0,0,0-.44,0l-1.73,1.73a3.25,3.25,0,0,1-2.34,1,3.28,3.28,0,0,1-2.34-1l-1.7-1.7a.27.27,0,0,0-.44,0l-1.61,1.61a3.31,3.31,0,0,1-4.68,0l-2.19-2.2A1.5,1.5,0,0,1,10.58,29l2.19,2.2a.31.31,0,0,0,.44,0l1.61-1.61a3.31,3.31,0,0,1,4.68,0l1.7,1.7a.27.27,0,0,0,.44,0l1.73-1.73a3.31,3.31,0,0,1,4.68,0L29.51,31A.31.31,0,0,0,30,31l2.17-2.17a3.3,3.3,0,0,1,4.68,0l2.74,2.74A1.49,1.49,0,0,1,39.54,33.71Z" />
      </svg>
    ),
    sauna: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M42.47,22.33V4.53a3,3,0,0,0-6,0V22.34a4.53,4.53,0,0,0-1.57,3.44,4.58,4.58,0,0,0,9.15,0A4.53,4.53,0,0,0,42.47,22.33Zm-3,6a2.51,2.51,0,0,1-1-4.81V10.12a1,1,0,1,1,2,0V23.48a2.51,2.51,0,0,1-1,4.81Z" />
        <rect x="16.04" y="17.99" width="3.52" height="21.06" />
        <rect x="10.52" y="17.99" width="3.52" height="21.06" />
        <rect x="10.52" y="41.05" width="3.52" height="5.52" />
        <rect x="21.56" y="17.99" width="3.52" height="21.06" />
        <path d="M27.08,18.34V39.05h4V24A6,6,0,0,0,27.08,18.34Z" />
        <path d="M8.52,46.23V41.05h-4A6,6,0,0,0,8.52,46.23Z" />
        <path d="M27.08,46.23a6,6,0,0,0,4-5.18h-4Z" />
        <rect x="16.04" y="41.05" width="3.52" height="5.52" />
        <rect x="21.56" y="41.05" width="3.52" height="5.52" />
        <path d="M4.5,24V39.05h4V18.34A6,6,0,0,0,4.5,24Z" />
        <path d="M13.58,5.91,12.4,4.74a1,1,0,0,0-1.41,0,1,1,0,0,0,0,1.41l1.17,1.18-.94.94a2,2,0,0,0,0,2.82l.89.9-1,1a1,1,0,0,0,0,1.41,1,1,0,0,0,1.41,0l1-1a2,2,0,0,0,0-2.83l-.89-.89.95-.94A2,2,0,0,0,13.58,5.91Z" />
        <path d="M17.81,4.74a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l1.18,1.18-.94.94a2,2,0,0,0,0,2.82l.89.9-1,1a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.71-.3l1-1a2,2,0,0,0,0-2.83L18,9.68,19,8.74a2,2,0,0,0,0-2.83Z" />
        <path d="M22,11.09l.89.9-1,1a1,1,0,1,0,1.42,1.41l1-1a2,2,0,0,0,0-2.83l-.89-.89.94-.94a2,2,0,0,0,0-2.83L23.22,4.74a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41L23,7.33,22,8.27A2,2,0,0,0,22,11.09Z" />
      </svg>
    ),
    billiard: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M45.4,37.59l-12-22.32a6,6,0,0,0-10.58,0C19.18,22.08,14.4,31,10.86,37.59a6,6,0,0,0,5.28,8.85h24A6,6,0,0,0,45.4,37.59ZM28,17.68a3.6,3.6,0,1,1-3.6,3.59A3.6,3.6,0,0,1,28,17.68Zm-9,25a3.6,3.6,0,1,1,3.6-3.6A3.6,3.6,0,0,1,19,42.72Zm.92-12.42a3.6,3.6,0,1,1,3.59,3.6A3.59,3.59,0,0,1,19.91,30.3ZM28,42.93a3.6,3.6,0,1,1,3.6-3.59A3.6,3.6,0,0,1,28,42.93Zm.92-12.63a3.6,3.6,0,1,1,3.6,3.6A3.6,3.6,0,0,1,28.94,30.3Zm8.11,12.42a3.6,3.6,0,1,1,3.6-3.6A3.6,3.6,0,0,1,37.05,42.72Z" />
        <path d="M11.13,20,2.06,29.24A2,2,0,0,0,4.92,32l9-9.23Z" />
        <polygon points="28.77 6.25 27.36 4.84 13.24 19.25 14.66 20.67 28.77 6.25" />
        <path d="M31.6,3.35A1,1,0,0,0,30.18,2L28.75,3.41l1.41,1.41Z" />
      </svg>
    ),
    'gas-supply': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M39.2,38.61c0-3-1.81-5.37-4.05-5.37H12.85c-2.23,0-4,2.41-4,5.37v.6H39.2Z" />
        <path d="M24,29.79A11.74,11.74,0,0,0,32.08,9.52h0L24,1.41l-8.1,8.1h0A11.74,11.74,0,0,0,24,29.79ZM19.62,17.17,24,12.79l4.38,4.38h0a6.36,6.36,0,1,1-8.74,0Z" />
        <path d="M42.13,42.47H5.87a2,2,0,1,0,0,4H42.13a2,2,0,0,0,0-4Z" />
      </svg>
    ),
    'electricity-supply': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M12.4,22H36.06a2,2,0,0,0-.2-4H33.75a6,6,0,0,0-4.61-4V3.59a2,2,0,0,0-4,0V14H16.4V3.59a2,2,0,1,0-4,0V14.06a5.94,5.94,0,0,0-4.61,4H5.67a2,2,0,0,0-.2,4H12.4Z" />
        <path d="M42.32,1.6a2,2,0,0,0-2,2V38.45a4,4,0,0,1-4,3.95H26.73A4,4,0,0,1,23,39.75h1.81A2,2,0,0,0,26,36.17h2a6,6,0,0,0,6-6V26H7.47v4.15a6,6,0,0,0,6,6h2a2,2,0,0,0,1.21,3.58h2.17a8,8,0,0,0,7.84,6.65h9.64a8,8,0,0,0,8-7.95V3.6A2,2,0,0,0,42.32,1.6Z" />
      </svg>
    ),
    'sewerage-supply': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M18.12,17.63v.18h12v-.18a16,16,0,0,0-16-16H7.53a6,6,0,0,0,0,12h6.59A4,4,0,0,1,18.12,17.63Z" />
        <path d="M40.53,34.32H34.12a4,4,0,0,1-4-4V27.65h-12v2.67a16,16,0,0,0,16,16h6.41a6,6,0,0,0,0-12Z" />
        <path d="M30.12,25.79h2.76v3.57a2,2,0,1,0,4,0V25.79h.24a3,3,0,0,0,0-6h-.24V16.22a2,2,0,0,0-4,0v3.57H17.82l-.31,0a3,3,0,0,0-.58,5.84,2.91,2.91,0,0,0,.89.14h12.3Z" />
      </svg>
    ),
    television: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <rect x="7.09" y="18.33" width="33.71" height="17.32" rx="3" />
        <path d="M39.8,13.33H33.89l6.22-8.69a2,2,0,1,0-3.25-2.33l-5.62,7.86L27.4,5.36a2,2,0,1,0-3.12,2.49l4.36,5.48H8.09a6,6,0,0,0-6,6V34.65a6,6,0,0,0,6,6H21.85v1.81H15a2,2,0,1,0,0,4H32.76a2,2,0,0,0,0-4H25.85V40.65H39.8a6,6,0,0,0,6-6V19.33A6,6,0,0,0,39.8,13.33Zm3,19.32a5,5,0,0,1-5,5H10.09a5,5,0,0,1-5-5V21.33a5,5,0,0,1,5-5H37.8a5,5,0,0,1,5,5Z" />
      </svg>
    ),
    'with-children': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M43.59,18.88a9.95,9.95,0,1,0-16.54-4.12l-2.67,2.67-8-8a6,6,0,0,0-8.41,0L6.36,11l8.81,8.82A13.39,13.39,0,1,0,28.61,33.23L37.43,42,39,40.51A6,6,0,0,0,39,32l-8-8,2.67-2.67A10,10,0,0,0,43.59,18.88ZM21.87,39.8a9.38,9.38,0,1,1,2.75-6.64A9.4,9.4,0,0,1,21.87,39.8Z" />
      </svg>
    ),
    'with-pets': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M33.85,25.69a9.89,9.89,0,0,0-19.25,0A9.89,9.89,0,0,0,18,44.85a9.8,9.8,0,0,0,6.18-2.18,9.89,9.89,0,1,0,9.63-17ZM21,37.38a4.1,4.1,0,0,1-.86-8.1,4.09,4.09,0,0,1,8.15,0,4.1,4.1,0,1,1-4.08,6.54A4.1,4.1,0,0,1,21,37.38Z" />
        <path d="M13.65,19.88a6,6,0,1,0-6,6A6,6,0,0,0,13.65,19.88Z" />
        <path d="M17.14,16.39a6,6,0,1,0-6-6A6,6,0,0,0,17.14,16.39Z" />
        <path d="M40.35,13.88a6,6,0,1,0,6,6A6,6,0,0,0,40.35,13.88Z" />
        <path d="M30.86,16.39a6,6,0,1,0-6-6A6,6,0,0,0,30.86,16.39Z" />
      </svg>
    ),
    dishwasher: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M20.81,29.45a7,7,0,0,0-2.3-2.15c-.22-.13-.4-.22-.59-.31A7,7,0,0,0,8,33.35v.09H22v-.09A6.92,6.92,0,0,0,20.81,29.45Zm9,0a7,7,0,0,0-2.3-2.15c-.22-.13-.4-.22-.59-.31a7.07,7.07,0,0,0-3.42-.62,10.1,10.1,0,0,1,.62.84A10.89,10.89,0,0,1,26,33.35v.09h5v-.09A6.92,6.92,0,0,0,29.81,29.45Zm9,0a7,7,0,0,0-6.31-3.08,10.1,10.1,0,0,1,.62.84A10.89,10.89,0,0,1,35,33.35v.09h5v-.09A6.92,6.92,0,0,0,38.81,29.45ZM1.5,16.38V33.44H4s0-.06,0-.09a11,11,0,0,1,15.52-10,11.2,11.2,0,0,1,9,0,11.21,11.21,0,0,1,9.06,0,11,11,0,0,1,6.43,10v.09h2.5V16.38ZM38.81,29.45a7,7,0,0,0-6.31-3.08,10.1,10.1,0,0,1,.62.84A10.89,10.89,0,0,1,35,33.35v.09h5v-.09A6.92,6.92,0,0,0,38.81,29.45Zm-9,0a7,7,0,0,0-2.3-2.15c-.22-.13-.4-.22-.59-.31a7.07,7.07,0,0,0-3.42-.62,10.1,10.1,0,0,1,.62.84A10.89,10.89,0,0,1,26,33.35v.09h5v-.09A6.92,6.92,0,0,0,29.81,29.45Zm-9,0a7,7,0,0,0-2.3-2.15c-.22-.13-.4-.22-.59-.31A7,7,0,0,0,8,33.35v.09H22v-.09A6.92,6.92,0,0,0,20.81,29.45Zm-19.31,8V40.5a6,6,0,0,0,6,6h33a6,6,0,0,0,6-6V37.44ZM40.5,1.5H7.5a6,6,0,0,0-6,6v4.88h45V7.5A6,6,0,0,0,40.5,1.5ZM17.41,8.6a2,2,0,1,1,0-2.83A2,2,0,0,1,17.41,8.6Zm8.91.58H21.68a2,2,0,0,1,0-4h4.64a2,2,0,0,1,0,4Zm7.1-.58a2,2,0,1,1,0-2.83A2,2,0,0,1,33.42,8.6Z" />
      </svg>
    ),
    'washing-machine': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M37.48,1.5h-27a6,6,0,0,0-6,6v4.88h39V7.5A6,6,0,0,0,37.48,1.5ZM23.58,9.19h-11a2,2,0,0,1,0-4h11a2,2,0,1,1,0,4ZM32,8.6a2,2,0,1,1,0-2.82A2,2,0,0,1,32,8.6Zm4.78,0a2,2,0,1,1,0-2.82A2,2,0,0,1,36.79,8.6Z" />
        <path d="M26.47,31.93a2,2,0,0,0,2,2h2.19a7,7,0,1,1,.05-4H28.47A2,2,0,0,0,26.47,31.93Z" />
        <path d="M4.48,16.38V40.5a6,6,0,0,0,6,6h27a6,6,0,0,0,6-6V16.38ZM24,42.84a11,11,0,1,1,11-11A11,11,0,0,1,24,42.84Z" />
      </svg>
    ),
    'guarded-building': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M42.5,29.55a4,4,0,0,0-4,4v9a4,4,0,0,0,8,0v-9A4,4,0,0,0,42.5,29.55Z" />
        <path d="M12.83,25H11.55a3,3,0,0,0-3,3V46.55h3.28a4,4,0,0,0,4-4V31.22l4.82-1.86,2.29-5.18L15.65,27A3,3,0,0,0,12.83,25Z" />
        <polygon points="23.29 28.33 26.78 26.98 29.06 21.81 25.58 23.16 23.29 28.33" />
        <polygon points="29.42 25.96 32.91 24.61 35.2 19.43 31.71 20.78 29.42 25.96" />
        <path d="M44.12,18.12A2,2,0,0,0,41.53,17l-3.69,1.43h0l-2.28,5.18L43,20.71A2,2,0,0,0,44.12,18.12Z" />
        <path d="M9.52,5.5a4,4,0,0,0-4-4h0a4,4,0,0,0-4,4V42.55a4,4,0,0,0,4,4h1V28a5,5,0,0,1,3-4.57Z" />
      </svg>
    ),
    security: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M45,16.81a20.78,20.78,0,0,0-3.78-12,12.88,12.88,0,0,1-6.71,1.85A13.07,13.07,0,0,1,24,1.5,13.14,13.14,0,0,1,13.52,6.67,13,13,0,0,1,6.79,4.82,21,21,0,0,0,9.57,32v0L24,46.5,38.44,32.05v0A20.94,20.94,0,0,0,45,16.81Zm-21-4a5.13,5.13,0,1,1-5.12,5.13A5.13,5.13,0,0,1,24,12.77ZM14.93,29.12a9.79,9.79,0,0,1,18.14,0Z" />
      </svg>
    ),
    'rubbish-chute': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M17.26,18.56c.49,0,.9,0,1.3.05A20.5,20.5,0,0,1,33.27,26.3a23,23,0,0,1,1.41,1.84h0a11.1,11.1,0,0,0,2.1-5.39,10.68,10.68,0,0,0-.13-3.23A11,11,0,0,0,31.43,12l0,5.19a1,1,0,0,1-.81,1,.67.67,0,0,1-.2,0,1,1,0,0,1-1-1L29.43,12,26,15.87a1.08,1.08,0,0,1-.56.31,1,1,0,0,1-.86-.25,1,1,0,0,1-.07-1.41L28,10.7a11.05,11.05,0,0,0-11.89,5.53l0,0a10.89,10.89,0,0,0-1,2.43c.56-.06,1.13-.1,1.71-.11Z" />
        <path d="M29.93,10l.79.3A4,4,0,0,0,35.9,8l.55-1.41L28.22,3.38l-.55,1.4A4,4,0,0,0,29.93,10Z" />
        <path d="M45.43,31.36a2,2,0,0,0-2.52-1.29L37.5,31.81l-1.76.56-.14.05L33.69,33c0-.14-.1-.28-.16-.41a17.52,17.52,0,0,0-15.18-11c-.36,0-.72,0-1.09,0h-.33A17.36,17.36,0,0,0,6.48,25.28V3.5A2,2,0,0,0,5.9,2.09,2,2,0,0,0,4.48,1.5a2,2,0,0,0-2,2v41a1.86,1.86,0,0,0,.1.61,2.33,2.33,0,0,0,.11.28,2,2,0,0,0,.62.73,1.48,1.48,0,0,0,.27.16,1.19,1.19,0,0,0,.26.11l.27.07.14,0a1.76,1.76,0,0,0,.56,0,1.28,1.28,0,0,0,.28-.07l.17-.06L31,38.1l.9,2.72a2,2,0,0,0,1.9,1.37,2.05,2.05,0,0,0,.64-.11,2,2,0,0,0,1.26-2.53l-.9-2.67,1.78-.57,1.92-.62,5.64-1.81A2,2,0,0,0,45.52,32,2.15,2.15,0,0,0,45.43,31.36Z" />
      </svg>
    ),
    lift: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <rect x="25.98" y="14.39" width="7.85" height="32.11" />
        <rect x="14.13" y="14.39" width="7.85" height="32.11" />
        <path d="M40.5,1.5H7.5a6,6,0,0,0-6,6v33a6,6,0,0,0,6,6h2.63V14.39a4,4,0,0,1,4-4h19.7a4,4,0,0,1,4,4V46.5H40.5a6,6,0,0,0,6-6V7.5A6,6,0,0,0,40.5,1.5ZM25.19,8H16.77a2,2,0,0,1,0-4h8.42a2,2,0,0,1,0,4Zm5.22,0a2,2,0,1,1,2-2A2,2,0,0,1,30.41,8Zm13.75,22.3L43,31.44a1,1,0,0,1-.71.29,1,1,0,0,1-.71-.29l-1.19-1.19a1,1,0,0,1,1.42-1.42l.48.49.49-.49a1,1,0,0,1,1.41,0A1,1,0,0,1,44.16,30.25Zm0-3.76a1,1,0,0,1-1.41,0L42.26,26l-.48.49a1,1,0,0,1-.71.29,1,1,0,0,1-.71-.29,1,1,0,0,1,0-1.42l1.19-1.19a1,1,0,0,1,1.42,0l1.19,1.19A1,1,0,0,1,44.16,26.49Z" />
      </svg>
    ),
    'room-furniture': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M40.5,19.75H7.5a6,6,0,0,0-6,6V37.14a6,6,0,0,0,4.27,5.74v1.69a2,2,0,0,0,4,0V43.14H38.18v1.43a2,2,0,0,0,4,0V42.89a6,6,0,0,0,4.32-5.75V25.75A6,6,0,0,0,40.5,19.75ZM30.74,25.9a1,1,0,0,1,1.41-1.41l.63.63.64-.63a1,1,0,0,1,1.41,1.41l-.63.63.63.64a1,1,0,0,1,0,1.41,1,1,0,0,1-.71.29,1,1,0,0,1-.7-.29L32.78,28l-.63.63a1,1,0,0,1-1.41,0,1,1,0,0,1,0-1.41l.63-.64ZM22,25.9a1,1,0,0,1,0-1.41,1,1,0,0,1,1.42,0l.63.63.63-.63a1,1,0,0,1,1.42,0,1,1,0,0,1,0,1.41l-.64.63.64.64a1,1,0,0,1,0,1.41,1,1,0,0,1-1.42,0L24,28l-.63.63a1,1,0,0,1-1.42,0,1,1,0,0,1,0-1.41l.64-.64Zm-8.78,0a1,1,0,0,1,1.41-1.41l.64.63.63-.63a1,1,0,0,1,1.41,1.41l-.63.63.63.64a1,1,0,0,1,0,1.41,1,1,0,0,1-.7.29,1,1,0,0,1-.71-.29L15.22,28l-.64.63a1,1,0,0,1-.7.29,1,1,0,0,1-.71-.29,1,1,0,0,1,0-1.41l.63-.64Zm29,10.34a2,2,0,0,1-2,2H7.82a2,2,0,0,1-2-2V31.35a2,2,0,0,1,4,0v2.89H38.18V31.35a2,2,0,0,1,4,0Z" />
        <path d="M22.08,9a1,1,0,0,1,1.11.22l2.53,2.63L35.87,6a1,1,0,0,1,1.22.16l2.3,2.36v-5a2,2,0,0,0-2-2H10.61a2,2,0,0,0-2,2V7.62l5.07,4.93ZM15.84,4.12a2,2,0,1,1-2,2A2,2,0,0,1,15.84,4.12Z" />
        <path d="M10.61,16.11H37.39a2,2,0,0,0,2-2v-2.8a.53.53,0,0,1-.1-.06L36.2,8.08,26.05,14a1,1,0,0,1-1.22-.17l-2.61-2.7-8.37,3.54a1,1,0,0,1-.39.08,1,1,0,0,1-.69-.29l-4.16-4v3.71A2,2,0,0,0,10.61,16.11Z" />
      </svg>
    ),
    'kitchen-furniture': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M8.73,28.57V22.82h2.54v.65a2,2,0,0,0,4,0v-.65a4,4,0,0,0-4-4H8.73a4,4,0,0,0-4,4v5.75H1.5v12a6,6,0,0,0,6,6H22.09v-18ZM19.22,32.5H16.79a1,1,0,0,1,0-2h2.43a1,1,0,0,1,0,2Z" />
        <path d="M26.09,28.57v18H40.5a6,6,0,0,0,6-6v-12ZM42.94,32.5H40.51a1,1,0,1,1,0-2h2.43a1,1,0,0,1,0,2Z" />
        <path d="M7.5,1.41a6,6,0,0,0-6,6v9H13.73v-15Zm3.24,13.07H8.31a1,1,0,0,1,0-2h2.43a1,1,0,0,1,0,2Z" />
        <path d="M17.73,1.41v15H30.25v-15Zm9.51,13.07H24.81a1,1,0,0,1,0-2h2.43a1,1,0,0,1,0,2Z" />
        <path d="M40.5,1.41H34.25v15H46.5v-9A6,6,0,0,0,40.5,1.41Zm2.87,13.07H40.94a1,1,0,0,1,0-2h2.43a1,1,0,0,1,0,2Z" />
      </svg>
    ),
    refrigerator: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M41,17.11V7.5a6,6,0,0,0-6-6H13a6,6,0,0,0-6,6v9.61ZM12.38,10.38H17a2,2,0,0,1,0,4H12.38a2,2,0,0,1,0-4Z" />
        <path d="M7,21.11V38.28a6,6,0,0,0,5.48,6v.35a2,2,0,0,0,4,0v-.33h15v.33a2,2,0,1,0,4,0v-.35a6,6,0,0,0,5.48-6V21.11Zm7.38,9.36a2,2,0,0,1-4,0V25.83a2,2,0,0,1,4,0Z" />
      </svg>
    ),
    internet: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <circle cx="24" cy="38.32" r="8" />
        <path d="M41,16.4a27.31,27.31,0,0,0-16.82-5.72A27.69,27.69,0,0,0,7,16.64a2,2,0,0,0,1.24,3.57,2,2,0,0,0,1.24-.43,23.62,23.62,0,0,1,29-.22A2,2,0,0,0,41,16.4Z" />
        <path d="M24.08,20.32A17.8,17.8,0,0,0,11,26a2,2,0,0,0,.08,2.82,2,2,0,0,0,2.82-.08,14,14,0,0,1,20.22-.14A2,2,0,0,0,37,25.78,17.85,17.85,0,0,0,24.08,20.32Z" />
        <path d="M44.7,7.94a36.64,36.64,0,0,0-41.42.31,2,2,0,1,0,2.28,3.28,32.64,32.64,0,0,1,36.9-.27A2,2,0,1,0,44.7,7.94Z" />
      </svg>
    ),
    'air-conditioner': (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="currentColor"
      >
        <path d="M40.5,1.26H7.5a6,6,0,0,0-6,6V21a6,6,0,0,0,6,6h33a6,6,0,0,0,6-6V7.26A6,6,0,0,0,40.5,1.26ZM9.05,5.64H39a2,2,0,0,1,0,4H9.05a2,2,0,0,1,0-4Zm-2,8.87a2,2,0,0,1,2-2H39a2,2,0,0,1,0,4H9.05A2,2,0,0,1,7.05,14.51Zm27.84,7.56a2,2,0,1,1,0-2.83A2,2,0,0,1,34.89,22.07Zm5.48,0a2,2,0,1,1,0-2.83A2,2,0,0,1,40.37,22.07Z" />
        <path d="M24,29.11a2,2,0,0,0-2,2V44.42a2,2,0,0,0,4,0V31.11A2,2,0,0,0,24,29.11Z" />
        <path d="M16.52,29.1a2,2,0,0,0-2,2v8.61a2,2,0,0,0,4,0V31.1A2,2,0,0,0,16.52,29.1Z" />
        <path d="M9.05,29.1a2,2,0,0,0-2,2V35a2,2,0,0,0,4,0V31.1A2,2,0,0,0,9.05,29.1Z" />
        <path d="M31.48,29.1a2,2,0,0,0-2,2v8.61a2,2,0,0,0,4,0V31.1A2,2,0,0,0,31.48,29.1Z" />
        <path d="M39,29.1a2,2,0,0,0-2,2V35a2,2,0,1,0,4,0V31.1A2,2,0,0,0,39,29.1Z" />
      </svg>
    )
  }[t];
};

const ObjectInformationContent = ({ data }) => {
  const [appState, setAppState] = useAppProvider();
  const locationState = useLocationState();
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientComment, setClientComment] = useState('');
  const [agreeWithPolicy, setAgreeWithPolicy] = useState(false);
  const clientNameId = useRef();
  const clientPhoneId = useRef();
  const { width } = useWindowDimensions();
  const setObjectsState = useObjectsDispatch();
  const [isModalShareVisible, setIsModalShareVisible] = useState(false);
  const [isModalMortgageVisible, setIsModalMortgageVisible] = useState(false);
  const [isModalMortgageSuccessVisible, setIsModalMortgageSuccessVisible] = useState(false);
  const m2Price = useRef(
    data.type === 'продажа' && data.object.area.total && data.object.area.total.value !== 0
      ? Math.round(data.price.value / data.object.area.total.value)
      : null
  ).current;
  const sotPrice = useRef(
    data.type === 'продажа' &&
      data.category !== 'дом' &&
      data.object.area['lot-area'] &&
      data.object.area['lot-area'].value !== 0
      ? Math.round(data.price.value / data.object.area['lot-area'].value)
      : null
  ).current;

  const getTitle = () => {
    const arr = [];

    arr.push(data.type === 'продажа' ? 'Купить' : 'Снять');

    switch (data.category) {
      default:
      case 'квартира':
        if (data.object.studio) arr.push('квартиру-студию');
        else if (data.object.rooms)
          arr.push(
            `${data.object.rooms}${
              data.object.rooms > 1 && data.object.rooms < 5 ? '-х' : ''
            } комнатную квартиру`
          );
        else arr.push('квартиру');

        break;

      case 'комната':
        if (data.object['rooms-offered'] === 1) arr.push('комнату');
        else arr.push(data.object['rooms-offered'], 'комнаты');

        arr.push(`в ${data.object.rooms} комнатной квартире`);

        break;

      case 'дом':
        if (data.object['floors-total']) arr.push(`${data.object['floors-total']} этажный дом`);
        else arr.push('дом');

        break;

      case 'участок':
        arr.push(
          `участок ${data.object.area['lot-area'].value} ${declOfNum(
            data.object.area['lot-area'].value,
            ['сотка', 'сотки', 'соток']
          )}`
        );

        break;

      case 'коммерция':
        arr.push(data.object['commercial-type']);

        break;
    }

    return arr.join(' ');
  };

  const handleShareClick = () => {
    if (navigator.share && isMobile) {
      navigator.share({
        title: document.title,
        text: document.querySelector('meta[name="description"]').content,
        url: `https://landlord.ru/object/${data['ads-number']}`
      });
    } else setIsModalShareVisible(true);
  };
  const handleFavoriteClick = () => {
    let newFavorites;

    if (appState.favorites.includes(data['ads-number'].toString())) {
      newFavorites = appState.favorites.filter((x) => x !== data['ads-number'].toString());
    } else {
      newFavorites = [data['ads-number'].toString(), ...appState.favorites];
    }

    setAppState({
      type: 'SET_APP',
      payload: {
        ...appState,
        favorites: newFavorites
      }
    });
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
  };
  const handleMortgageFormSubmit = () => {
    clientNameId.current.setError(clientName.length <= 3);
    clientPhoneId.current.setError(clientPhone.length !== 11);

    if (clientName.length > 3 && clientPhone.length === 11 && agreeWithPolicy) {
      submitClientRequest({
        clientName,
        clientPhone: clientPhone,
        clientComment,
        pathName: `/object/${data['ads-number']}`,
        whatComponent: 'ObjectInformationOwner',
        mortgage: true,
        location: locationState.name,
        platform: 'web'
      }).then((isSubmitted) => {
        if (isSubmitted) {
          setIsModalMortgageVisible(false);
          setIsModalMortgageSuccessVisible(true);
        }
      });
    }
  };

  return (
    <section className={styles.column}>
      <Modal
        title="Заявка на консультацию"
        visibility={isModalMortgageSuccessVisible}
        onClose={() => setIsModalMortgageSuccessVisible(false)}
      >
        <div style={{ marginBottom: 18 }}>Спасибо за заявку. Мы скоро с Вами свяжемся!</div>
        <img
          src="/images/thumbUp.png"
          alt=""
          style={{ padding: 50, width: '100%', boxSizing: 'border-box' }}
        />
      </Modal>
      <Modal
        title="Заявка на консультацию"
        button="Отправить"
        action={handleMortgageFormSubmit}
        visibility={isModalMortgageVisible}
        onClose={() => setIsModalMortgageVisible(false)}
        buttonDisabled={!agreeWithPolicy}
      >
        <div style={{ marginBottom: 18 }}>
          Оставьте свои контактные данные и мы с Вами свяжемся в самые кратчайшие сроки, поможем
          оформить ипотеку при необходимости!
        </div>
        <Input
          title="Имя"
          inputs={[
            {
              placeholder: 'Василий Петрович',
              value: clientName,
              type: 'text'
            }
          ]}
          isWrappedStyle
          ref={clientNameId}
          onChange={(v) => setClientName(v[0])}
        />
        <Input
          title="Телефон"
          inputs={[
            {
              placeholder: locationState.phone,
              value: clientPhone,
              mask: 'phone',
              type: 'tel'
            }
          ]}
          isWrappedStyle
          ref={clientPhoneId}
          onChange={(v) => setClientPhone(v[0])}
        />
        <Textarea
          title="Коммментарий"
          placeholder="Например, «Хочу приобрести ваш объект в ипотеку, требуется консультация»"
          autoHeight
          isWrappedStyle
          onChange={(v) => setClientComment(v)}
        />
        <FormAgreement onChange={setAgreeWithPolicy} />
      </Modal>
      <div className={styles.mobileWrapper}>
        <div className={styles.breadcrumbs}>
          <Link
            href="/objects"
            passHref
            role="button"
            tabIndex={0}
            className={styles.breadcrumbsItem}
            onClick={() => {
              setObjectsState({
                type: 'SET_FILTER',
                payload: {}
              });
            }}
          >
            Недвижимость
          </Link>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.breadcrumbsSplitter}
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
          <Link
            href={`/objects/${data.type === 'аренда' ? 'rent' : 'buy'}`}
            passHref
            role="button"
            tabIndex={0}
            className={styles.breadcrumbsItem}
            onClick={() => {
              setObjectsState({
                type: 'SET_FILTER',
                payload: {
                  type: data.type
                }
              });
            }}
          >
            {data.type === 'продажа' ? 'Продажа' : 'Аренда'}
          </Link>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.breadcrumbsSplitter}
          >
            <line x1="5" y1="12" x2="19" y2="12" />
            <polyline points="12 5 19 12 12 19" />
          </svg>
          <Link
            href={`/objects/${data.type === 'аренда' ? 'rent' : 'buy'}/${
              categoryToSEO(data.category).link
            }`}
            passHref
            role="button"
            tabIndex={0}
            className={styles.breadcrumbsItem}
            onClick={() => {
              setObjectsState({
                type: 'SET_FILTER',
                payload: {
                  type: data.type,
                  category: data.category
                }
              });
            }}
          >
            {categoryToSEO(data.category).text}
          </Link>
          {data.category === 'квартира' && (
            <>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={styles.breadcrumbsSplitter}
              >
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
              <Link
                href={`/objects/${data.type === 'аренда' ? 'rent' : 'buy'}/flats/${
                  flatsToSEO(data.object.studio ? 'студия' : data.object.rooms).link
                }`}
                passHref
                role="button"
                tabIndex={0}
                className={styles.breadcrumbsItem}
                onClick={() => {
                  setObjectsState({
                    type: 'SET_FILTER',
                    payload: {
                      type: data.type,
                      category: data.category,
                      studio: data.object.studio,
                      rooms: [data.object.rooms.toString()]
                    }
                  });
                }}
              >
                {flatsToSEO(data.object.studio || data.object.rooms).text}
              </Link>
            </>
          )}
        </div>
        <h1 className="clear">{getTitle()}</h1>
        <div className={styles.system}>
          {getTimeInterval(data.interval) && (
            <span className={styles.systemTag}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 8 }}
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {getTimeInterval(data.interval)}
            </span>
          )}
          <span className={styles.systemTag}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: 8 }}
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>{' '}
            {data.views} {declOfNum(data.views, ['просмотр', 'просмотра', 'просмотров'])}
          </span>
          <span className={styles.systemTag}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ marginRight: 8 }}
            >
              <line x1="4" y1="9" x2="20" y2="9" />
              <line x1="4" y1="15" x2="20" y2="15" />
              <line x1="10" y1="3" x2="8" y2="21" />
              <line x1="16" y1="3" x2="14" y2="21" />
            </svg>
            {data['lot-number']} лот
          </span>
        </div>
        <div className={styles.address}>{objectAddressToText(data.location)}</div>
        <ObjectGallery
          isActual={data.isActual}
          images={data.images}
          video={data['video-review']}
          raw={data}
        />
        <div className={styles.flags}>
          {width < 992 && Boolean(data.isActual) && (
            <Fragment>
              <div className={styles.flag}>
                <div className={styles.flagTitle}>
                  {toMoney(data.price.value)} {data.price.currency}
                  {getPriceDiff(data.type, data.price) && getPriceDiff(data.type, data.price).icon}
                </div>
                {getPriceDiff(data.type, data.price) && (
                  <div className={styles.flagSubtitle} style={{ fontSize: 14 }}>
                    {toMoney(getPriceDiff(data.type, data.price).value)} {data.price.currency}
                  </div>
                )}
                <div className={styles.flagDescription}>Стоимость</div>
              </div>
              {m2Price && (
                <div className={styles.flag}>
                  <div className={styles.flagTitle}>
                    {toMoney(m2Price)} {data.price.currency}/{data.object.area.total.unit}
                  </div>
                  <div className={styles.flagDescription}>
                    Стоимость за {data.object.area.total.unit}
                  </div>
                </div>
              )}
              {sotPrice && (
                <div className={styles.flag}>
                  <div className={styles.flagTitle}>
                    {toMoney(sotPrice)} {data.price.currency}/{data.object.area['lot-area'].unit}
                  </div>
                  <div className={styles.flagDescription}>
                    Стоимость за {data.object.area['lot-area'].unit}
                  </div>
                </div>
              )}
            </Fragment>
          )}
          {data.labels.map((value, index) => (
            <div className={styles.flag} key={index}>
              <div className={styles.flagTitle}>
                {value.value} {value.unit || ''}
              </div>
              <div className={styles.flagDescription}>{value.text}</div>
            </div>
          ))}
        </div>
        <div className={styles.mobileActions}>
          <Modal
            title="Поделиться объектом"
            button="Скопировать ссылку"
            action={(e) => {
              copyToClipboard(`https://landlord.ru/object/${data['ads-number']}`);

              e.target.innerText = 'Ссылка скопирована';
              e.target.style.cssText = 'opacity: 0.4; pointer-events: none;';
              setTimeout(() => {
                e.target.innerText = 'Скопировать ссылку';
                e.target.style.cssText = '';
              }, 2000);
            }}
            visibility={isModalShareVisible}
            onClose={() => setIsModalShareVisible(false)}
          >
            <div>Выберите удобный для Вас сервис и нажмите на него</div>
            <div className={styles.modalButtons}>
              <a
                className={[styles.modalButton, styles.vk].join(' ')}
                href={`https://vk.com/share.php?url=https://landlord.ru/object/${data['ads-number']}`}
                target="_blank"
                rel="noreferrer"
              />
              <a
                className={[styles.modalButton, styles.ok].join(' ')}
                href={`https://connect.ok.ru/offer?url=https://landlord.ru/object/${data['ads-number']}`}
                target="_blank"
                rel="noreferrer"
              />
              <a
                className={[styles.modalButton, styles.tg].join(' ')}
                href={`https://t.me/share/url?url=https://landlord.ru/object/${data['ads-number']}`}
                target="_blank"
                rel="noreferrer"
              />
            </div>
          </Modal>
          <Button
            color={appState.favorites.includes(data['ads-number'].toString()) ? 'red' : 'gray'}
            onClick={handleFavoriteClick}
            style={{ marginTop: 8 }}
            fullWidth
          >
            {appState.favorites.includes(data['ads-number'].toString())
              ? 'Удалить из избранного'
              : 'Добавить в избранное'}
          </Button>
          <Button color="gray" onClick={handleShareClick} style={{ marginTop: 8 }} fullWidth>
            Поделиться объектом
          </Button>
        </div>
      </div>
      <div className={styles.description}>
        <h3 className={styles.descriptionTitle}>Описание</h3>
        <div className={styles.descriptionText}>
          {data.description
            .replace(/\n\n/g, '\n')
            .split('\n')
            .map((value, index) => (
              <p key={index}>{value}</p>
            ))}
        </div>
      </div>
      {data.object && Object.entries(data.object) && (
        <div className={styles.information}>
          <h3>{data.category.charAt(0).toUpperCase() + data.category.slice(1)}</h3>
          <ul className={styles.informationList}>
            {Object.entries(data.object).map((value, index) => {
              if (value[0] === 'exclusive') return false;

              if (value[0] === 'studio')
                return value[1] ? (
                  <li className={styles.informationListItem} key={index}>
                    <span className={styles.informationListHeading}>Комнат в квартире</span>
                    <div className={styles.informationListAnswer}>студия</div>
                  </li>
                ) : (
                  false
                );

              if (value[0] === 'area') {
                return Object.entries(value[1]).map((v, i) => (
                  <li className={styles.informationListItem} key={i}>
                    <span className={styles.informationListHeading}>
                      {language[v[0]] || value[0]}
                    </span>
                    <div className={styles.informationListAnswer}>
                      {typeOf(v[1]) === 'object'
                        ? `${v[1].value} ${v[1].unit}`
                        : typeOf(v[1]) === 'array'
                          ? v[1].join(', ')
                          : v[1]}
                    </div>
                  </li>
                ));
              }

              return (
                <li className={styles.informationListItem} key={index}>
                  <span className={styles.informationListHeading}>
                    {language[value[0]] || value[0]}
                  </span>
                  <div className={styles.informationListAnswer}>
                    {typeOf(value[1]) === 'object'
                      ? `${value[1].value} ${value[1].unit}`
                      : typeOf(value[1]) === 'array'
                        ? value[1].join(', ')
                        : value[1]}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {data.building && Object.entries(data.building) && (
        <div className={styles.information}>
          <h3>Здание</h3>
          <ul className={styles.informationList}>
            {Object.entries(data.building).map((value, index) => (
              <li className={styles.informationListItem} key={index}>
                <span className={styles.informationListHeading}>
                  {language[value[0]] || value[0]}
                </span>
                <div className={styles.informationListAnswer}>{value[1]}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.options && !!Object.keys(data.options) && (
        <div className={styles.information}>
          <div className={styles.informationGroup}>
            {Object.entries(data.options).map((value, index) => (
              <div className={styles.informationGroupItem} key={index}>
                {optionToIcon(value[0])}
                <div className={styles.informationGroupItemText}>
                  {language[value[0]] || value[0]}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {Boolean(data.isActual) && (
        <LazyLoad>
          <div className={styles.map}>
            <h3>Расположение</h3>
            <div className={styles.mapContainer}>
              <ObjectMap point={[data.location.latitude, data.location.longitude]} />
            </div>
          </div>
          {data.type === 'продажа' && data.mortgage.payment > 0 && (
            <ObjectMortgage data={data} onSelect={() => setIsModalMortgageVisible(true)} />
          )}
        </LazyLoad>
      )}
    </section>
  );
};

export default ObjectInformationContent;
